import { ChevronRight as ChevronRightIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Box, Checkbox, IconButton, Skeleton, TableCell, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

import { CellRenderParams, ColumnWidths, DataTableColumn, ExpandedRows, NestedRowData, SelectionModel } from '../types';
import { calculateStickyColumnLeft, calculateStickyColumnRight, hasStickyColumns } from '../utils';

interface DataTableRowProps<T = any> {
    row: T | NestedRowData;
    columns: DataTableColumn<T>[];
    rowIndex: number;
    isLoading?: boolean;
    columnWidths: ColumnWidths;
    columnWidthStyles: Record<string, { width?: string; minWidth?: string; flexGrow?: number }>;
    selectionModel: SelectionModel;
    expandedRows: ExpandedRows;
    onRowSelect: (rowId: string | number) => void;
    onRowExpand: (rowId: string | number) => void;
    onRowClick?: (row: T, event: React.MouseEvent) => void;
    onRowDoubleClick?: (row: T, event: React.MouseEvent) => void;
    getRowProps?: (row: T, index: number) => any;
    getCellProps?: (params: CellRenderParams<T>) => any;
    checkboxSelection: boolean;
    treeData: boolean;
    dense: boolean;
    rowHeight: number | string;
    keyboardNavigation: boolean;
    isRowSelectable?: (row: any) => boolean;
    collapsible?: boolean;
    collapseAnchorColumn?: string;
    collapsibleExpandedRows?: Set<string | number>;
    onToggleCollapsibleRow?: (rowId: string) => void;
}

const StyledTableRow = styled(TableRow, {
    shouldForwardProp: prop => !['isSelected', 'isExpandable'].includes(prop as string),
})<{
    isSelected?: boolean;
    isExpandable?: boolean;
}>(({ theme, isSelected, isExpandable }) => ({
    cursor: isExpandable ? 'pointer' : 'default',
    listStyle: 'none', // Remove any list styling that might cause dots
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        // Target sticky columns when row is hovered
        '& .MuiTableCell-root': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    ...(isSelected && {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            '& .MuiTableCell-root': {
                backgroundColor: theme.palette.action.hover,
            },
        },
    }),
}));

const StyledTableCell = styled(TableCell)<{ level?: number }>(({ theme, level = 0 }) => ({
    borderBottom: `1px solid ${theme.palette.border.main}`,
    paddingLeft: level > 0 ? `${16 + level * 24}px` : '16px',
    position: 'relative',
    listStyle: 'none', // Remove any list styling that might cause dots
    '&::before, &::after': {
        display: 'none', // Remove any pseudo-elements that might create dots
    },
}));

const ExpandIconContainer = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: 8,
    width: 24,
    height: 24,
});

export const DataTableRow = <T extends Record<string, any> = any>({
    row,
    columns,
    rowIndex,
    columnWidths,
    columnWidthStyles,
    isLoading,
    selectionModel,
    expandedRows,
    onRowSelect,
    onRowExpand,
    onRowClick,
    onRowDoubleClick,
    getRowProps,
    getCellProps,
    checkboxSelection,
    treeData,
    dense,
    rowHeight,
    keyboardNavigation,
    collapsible,
    collapseAnchorColumn,
    collapsibleExpandedRows,
    onToggleCollapsibleRow,
    isRowSelectable,
}: DataTableRowProps<T>) => {
    const isNestedRow = treeData && typeof row === 'object' && 'id' in row;
    const nestedRow = row as NestedRowData;
    const actualRow = isNestedRow ? nestedRow.data : row;
    const rowId = isNestedRow ? nestedRow.id : row._id || row.id || rowIndex; // Fallback to index if no ID is present
    const level = isNestedRow ? nestedRow.level || 0 : 0;
    const hasChildren = isNestedRow && nestedRow.hasChildren;
    const isExpanded = isNestedRow && expandedRows[nestedRow.id];
    const isSelected = selectionModel.selectedRows.includes(rowId);

    // Check if any column is sticky to determine if checkbox should also be sticky
    const hasStickyCols = hasStickyColumns(columns);

    const handleRowClick = (event: React.MouseEvent) => {
        if (onRowClick) {
            onRowClick(actualRow as T, event);
        }
    };

    const handleRowDoubleClick = (event: React.MouseEvent) => {
        if (onRowDoubleClick) {
            onRowDoubleClick(actualRow as T, event);
        }
    };

    const handleCheckboxChange = () => {
        onRowSelect(rowId);
    };

    const handleExpandClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        onRowExpand(rowId);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!keyboardNavigation) return;

        switch (event.key) {
            case 'Enter':
            case ' ':
                if (hasChildren) {
                    event.preventDefault();
                    onRowExpand(rowId);
                }
                break;
            case 'ArrowRight':
                if (hasChildren && !isExpanded) {
                    event.preventDefault();
                    onRowExpand(rowId);
                }
                break;
            case 'ArrowLeft':
                if (hasChildren && isExpanded) {
                    event.preventDefault();
                    onRowExpand(rowId);
                }
                break;
        }
    };

    const renderCellContent = (
        column: DataTableColumn<T>,
        columnIndex: number,
        columnStyle: { width?: string; minWidth?: string; flexGrow?: number },
    ) => {
        const value = column.field ? actualRow[column.field] : undefined;

        // Get row ID for collapsible functionality
        const currentRowId = actualRow._id || actualRow.id || rowId;
        const isCollapsibleExpanded =
            collapsible && collapsibleExpandedRows ? collapsibleExpandedRows.has(currentRowId) : false;
        const isCollapseAnchor = collapsible && column.id === collapseAnchorColumn;

        const cellParams: CellRenderParams<T> = {
            value,
            row: actualRow as T,
            column,
            rowIndex,
            columnIndex,
            // Add collapsible information for collapse anchor column
            ...(isCollapseAnchor && {
                isExpanded: isCollapsibleExpanded,
                toggleExpanded: () => onToggleCollapsibleRow?.(currentRowId),
                expandAll: () => {}, // Will be provided by DataTable if needed
                collapseAll: () => {}, // Will be provided by DataTable if needed
                expandedCount: collapsibleExpandedRows?.size || 0,
                totalRows: 0, // Will be calculated if needed
            }),
        };

        const shouldWrap = Boolean(column.wrap);
        const containerSx = {
            ...columnStyle,
            maxWidth: column.maxWidth,
            overflow: 'hidden',
            ...(shouldWrap
                ? { whiteSpace: 'normal' as const }
                : { textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }),
        };

        // For tree data, always add expand button to first column if row has children
        if (columnIndex === 0 && treeData && hasChildren) {
            const expandButton = (
                <ExpandIconContainer>
                    <IconButton
                        size="small"
                        onClick={handleExpandClick}
                        sx={{ padding: '2px', marginRight: 1 }}
                        aria-label={isExpanded ? 'Collapse row' : 'Expand row'}>
                        {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </IconButton>
                </ExpandIconContainer>
            );

            // If there's a custom cell renderer, wrap it with the expand button
            if (column.renderCell) {
                const customContent = column.renderCell(cellParams);
                return (
                    <Box display="flex" alignItems="center" sx={containerSx}>
                        {expandButton}
                        <Box
                            flex={1}
                            sx={{
                                minWidth: 0, // Allow shrinking below content size
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: shouldWrap ? 'normal' : 'nowrap',
                            }}>
                            {customContent}
                        </Box>
                    </Box>
                );
            }

            // Default rendering with expand button
            return (
                <Box display="flex" alignItems="center" sx={containerSx}>
                    {expandButton}
                    <Box
                        flex={1}
                        sx={{
                            minWidth: 0, // Allow shrinking below content size
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                        {value}
                    </Box>
                </Box>
            );
        }

        // For non-expandable cells or non-first columns, use custom renderer or default
        if (column.renderCell) {
            return <Box sx={containerSx}>{column.renderCell(cellParams)}</Box>;
        }

        return (
            <span
                style={{
                    display: 'inline-block',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                {value}
            </span>
        );
    };

    const rowProps = getRowProps ? getRowProps(actualRow as T, rowIndex) : {};

    return (
        <StyledTableRow
            hover
            selected={isSelected}
            isSelected={isSelected}
            isExpandable={hasChildren}
            onClick={handleRowClick}
            onDoubleClick={handleRowDoubleClick}
            onKeyDown={handleKeyDown}
            tabIndex={keyboardNavigation ? 0 : -1}
            role="row"
            aria-selected={isSelected}
            aria-expanded={treeData ? isExpanded : undefined}
            sx={{
                height: typeof rowHeight === 'number' ? `${rowHeight}px` : rowHeight,
            }}
            {...rowProps}>
            {checkboxSelection && (
                <StyledTableCell
                    className={hasStickyCols ? 'sticky-column' : undefined}
                    style={{
                        textOverflow: 'unset',
                        width: 48, // Fixed width for checkbox column
                        flex: '0 0 48px', // Prevent checkbox column from resizing with other columns
                    }}
                    sx={theme => ({
                        position: hasStickyCols ? 'sticky' : 'relative',
                        left: hasStickyCols ? 0 : 'auto',
                        zIndex: hasStickyCols ? 101 : 'auto', // Higher than sticky columns to stay on top
                        backgroundColor: isSelected ? theme.palette.action.hover : theme.palette.tableRow.main, // Remove individual cell hover for sticky columns - row hover will handle it
                        ...(!hasStickyCols && {
                            '&:hover': {
                                backgroundColor: isSelected ? theme.palette.action.hover : theme.palette.tableRow.main,
                            },
                        }),
                    })}>
                    {isLoading ? (
                        <Skeleton variant="rectangular" width={24} height={24} />
                    ) : (
                        <Checkbox
                            checked={isSelected}
                            onChange={handleCheckboxChange}
                            color="primary"
                            size={dense ? 'small' : 'medium'}
                            disabled={isRowSelectable ? !isRowSelectable(actualRow) : false}
                            inputProps={{ 'aria-label': 'Select row' }}
                        />
                    )}
                </StyledTableCell>
            )}

            {columns.map((column, columnIndex) => {
                const columnStyle = columnWidthStyles[column.id];
                // Calculate left position for left-sticky columns
                const stickyLeft = calculateStickyColumnLeft(
                    column,
                    columns,
                    columnIndex,
                    columnWidths,
                    checkboxSelection,
                    dense,
                );
                // Calculate right position for right-sticky columns
                const stickyRight = calculateStickyColumnRight(column, columns, columnIndex, columnWidths);
                const isSticky = column.sticky || column.stickyRight;
                const cellProps = getCellProps
                    ? getCellProps({
                          value: column.field ? actualRow[column.field] : undefined,
                          row: actualRow as T,
                          column,
                          rowIndex,
                          columnIndex,
                      })
                    : {};

                return (
                    <StyledTableCell
                        key={column.id}
                        align={column.align || 'left'}
                        level={columnIndex === 0 ? level : 0}
                        className={isSticky ? 'sticky-column' : undefined}
                        sx={theme => ({
                            // Apply the calculated width styles
                            ...columnStyle,
                            // Override with any explicit maximum constraints
                            maxWidth: column.maxWidth,
                            position: isSticky ? 'sticky' : 'relative',
                            left: column.sticky ? stickyLeft : column.stickyRight ? 0 : 'auto',
                            right: column.stickyRight ? `0` : 'auto',
                            zIndex: isSticky ? 100 : 'auto',
                            backgroundColor: isSelected ? theme.palette.action.hover : theme.palette.tableRow.main,
                            overflow: 'hidden',
                            ...(column.wrap
                                ? { whiteSpace: 'normal' as const, verticalAlign: 'top' as const }
                                : { textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }),
                            boxSizing: 'border-box',
                            // Only apply individual cell hover for non-sticky columns
                            // Sticky columns will be handled by row hover
                            ...(!isSticky && {
                                '&:hover': {
                                    backgroundColor: isSelected ? theme.palette.action.hover : 'inherit',
                                },
                            }),
                            verticalAlign: 'middle',
                        })}
                        {...cellProps}>
                        {isLoading ? (
                            <Skeleton variant="rounded" width="80%" height={24} />
                        ) : (
                            renderCellContent(column, columnIndex, columnStyle)
                        )}
                    </StyledTableCell>
                );
            })}
        </StyledTableRow>
    );
};

export default DataTableRow;
